import React from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { motion } from 'framer-motion';

const Hero = () => {
  const heroImageUrl = "https://images.unsplash.com/photo-1616627547584-bf28cee262db?auto=format&fit=crop&q=85&w=1600";
  const cropImageUrl = "https://images.unsplash.com/photo-1620189507195-68309c04c4d0?auto=format&fit=crop&q=85&w=400";

  return (
    <section className="relative bg-slate-900 pt-16 pb-24 lg:pt-32 lg:pb-40 overflow-hidden">
      <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/carbon-fibre.png')] opacity-20" aria-hidden="true"></div>
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-full bg-[radial-gradient(circle_at_20%_30%,_rgba(79,70,229,0.15),_transparent_50%)]" aria-hidden="true"></div>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-indigo-500/10 border border-indigo-500/20 text-indigo-400 mb-8">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-indigo-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-indigo-500"></span>
              </span>
              <span className="mono-label text-[10px]">Workspace v2.4 Online</span>
            </div>
            
            <h1 className="text-5xl lg:text-7xl font-extrabold text-white tracking-tight leading-[1.1] mb-6">
              Precision Tooling <br/>
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 via-cyan-400 to-indigo-300">
                For Performance.
              </span>
            </h1>
            
            <p className="text-slate-400 text-lg lg:text-xl max-w-xl mb-10 leading-relaxed">
              DeskHarbor engineers modular components that eliminate physiological friction and cognitive load. Build your professional harbor today.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4">
              <button className="px-8 py-4 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-xl flex items-center justify-center gap-3 transition-all transform hover:scale-105 active:scale-95 shadow-xl shadow-indigo-500/20">
                Configure Platform <SafeIcon icon={FiIcons.FiArrowRight} />
              </button>
              <button className="px-8 py-4 bg-slate-800 hover:bg-slate-750 text-white font-bold rounded-xl border border-slate-700 transition-all flex items-center justify-center gap-2">
                Browse Registry
              </button>
            </div>

            <div className="mt-16 grid grid-cols-3 gap-8 border-t border-slate-800/60 pt-8">
              {[
                { label: 'Units Deployed', val: '14.2k+' },
                { label: 'System Uptime', val: '99.9%' },
                { label: 'User Rating', val: '4.92/5' }
              ].map((stat, i) => (
                <div key={i}>
                  <div className="text-2xl font-mono font-bold text-white mb-1">{stat.val}</div>
                  <div className="mono-label text-slate-500">{stat.label}</div>
                </div>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="relative"
          >
            <div className="relative rounded-2xl overflow-hidden border border-slate-700 shadow-2xl bg-slate-800">
              <picture>
                <source 
                  type="image/avif" 
                  srcSet={`${heroImageUrl}&fm=avif&w=800 800w, ${heroImageUrl}&fm=avif&w=1200 1200w, ${heroImageUrl}&fm=avif&w=1600 1600w`} 
                  sizes="(max-width: 1024px) 100vw, 50vw"
                />
                <source 
                  type="image/webp" 
                  srcSet={`${heroImageUrl}&fm=webp&w=800 800w, ${heroImageUrl}&fm=webp&w=1200 1200w, ${heroImageUrl}&fm=webp&w=1600 1600w`} 
                  sizes="(max-width: 1024px) 100vw, 50vw"
                />
                <img 
                  src={heroImageUrl}
                  alt="Modern professional workspace featuring modular trays and a monitor riser on a wooden desk" 
                  className="w-full aspect-[4/3] object-cover"
                  width="1600"
                  height="1200"
                  fetchPriority="high"
                  loading="eager"
                  decoding="async"
                />
              </picture>
              
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 to-transparent pointer-events-none"></div>

              <motion.div 
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 1, duration: 0.5 }}
                className="absolute -bottom-6 -left-6 w-32 h-32 md:w-40 md:h-40 rounded-xl overflow-hidden border-4 border-slate-900 shadow-2xl z-20 hidden sm:block bg-slate-800"
              >
                <picture>
                  <source type="image/avif" srcSet={`${cropImageUrl}&fm=avif&w=200`} />
                  <source type="image/webp" srcSet={`${cropImageUrl}&fm=webp&w=200`} />
                  <img 
                    src={cropImageUrl} 
                    alt="Close-up detail of a modular charging dock with premium metallic finish" 
                    className="w-full h-full object-cover"
                    width="160"
                    height="160"
                    loading="eager"
                    decoding="async"
                  />
                </picture>
                <div className="absolute top-2 right-2">
                  <div className="bg-indigo-600 text-[8px] font-bold text-white px-1.5 py-0.5 rounded uppercase tracking-tighter">Detail</div>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Hero;